import { createColumnHelper } from '@tanstack/react-table';
import { Check, Loader2, Pencil, X } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { toast } from 'sonner';

import {
  type LineItem,
  useGetInvoiceLineItems,
} from '~entities/payment/api/get-invoice-line-items';
import { useUpdateSubscriptionInvoiceLineItems } from '~entities/student/api/update-subscription-invoices';

import { useGetCurrency } from '~shared/api/currency-api';
import { InvoiceDiscountManager } from '~shared/components/discount-manager';
import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';

import { DeleteLineItem } from './delete-line-item';

interface EditUpcomingTransactionProps {
  transactionId: string;
  internalTransactionId: string;
}
const columnHelper = createColumnHelper<LineItem>();

export const EditUpcomingTransaction = forwardRef<
  HTMLButtonElement,
  EditUpcomingTransactionProps
>(({ transactionId }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<{
    productName: string;
    type: string;
  } | null>(null);
  const [editAmount, setEditAmount] = useState<string>('');
  const [amountError, setAmountError] = useState<string | null>(null);

  const { data: currencyData } = useGetCurrency();

  const { data: invoiceLineItems, isLoading } = useGetInvoiceLineItems({
    invoiceId: transactionId,
  });

  const { mutateAsync: updateSubscriptionInvoiceLineItems, isPending } =
    useUpdateSubscriptionInvoiceLineItems();

  const handleEditClick = (item: LineItem) => {
    setEditingItem({ productName: item.productName, type: item.type });
    setEditAmount(item.amount);
    setAmountError(null);
  };

  const validateAmount = (value: string): boolean => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 1) {
      setAmountError('Amount must be at least 1');
      return false;
    }
    setAmountError(null);
    return true;
  };

  const handleSaveEdit = async (item: LineItem) => {
    if (!validateAmount(editAmount)) {
      return;
    }

    try {
      await updateSubscriptionInvoiceLineItems({
        invoiceId: transactionId,
        lineItems: [
          {
            productName: item.productName,
            amount: Number(editAmount),
            type: item.type,
          },
        ],
      });
      toast.success('Line item updated successfully');
      setEditingItem(null);
    } catch (error) {
      console.error(error);
      toast.error('Failed to update line item');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setAmountError(null);
  };
  const columns = [
    columnHelper.accessor('productName', { header: 'Name' }),
    columnHelper.accessor('type', { header: 'Type' }),
    columnHelper.accessor('amount', {
      header: 'Amount',
      cell: ({ row }) =>
        editingItem?.productName === row.original.productName &&
        editingItem.type === row.original.type ? (
          <div>
            <div className='flex items-center gap-2'>
              <Input
                type='number'
                value={editAmount}
                onChange={(event) => {
                  setEditAmount(event.target.value);
                  validateAmount(event.target.value);
                }}
                className='w-24'
                step='0.01'
                min='1'
                autoFocus
              />
              <Button
                size='icon'
                variant='ghost'
                onClick={() => {
                  void handleSaveEdit(row.original);
                }}
                disabled={!!amountError || isPending}
              >
                {isPending ? (
                  <Loader2 className='size-4 animate-spin' />
                ) : (
                  <Check className='size-4 text-green-500' />
                )}
              </Button>
              <Button size='icon' variant='ghost' onClick={handleCancelEdit}>
                <X className='size-4 text-red-500' />
              </Button>
            </div>
            {amountError ? (
              <p className='text-xs text-red-500'>{amountError}</p>
            ) : null}
          </div>
        ) : (
          <div className='flex items-center gap-2'>
            <span>
              {currencyData?.symbol}
              {row.original.amount}
            </span>
            <Button
              size='icon'
              variant='ghost'
              onClick={() => {
                handleEditClick(row.original);
              }}
            >
              <Pencil className='size-3.5' />
              <span className='sr-only'>Edit line item</span>
            </Button>
            <DeleteLineItem
              transactionId={transactionId}
              type={row.original.type}
            />
          </div>
        ),
    }),
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant='outline' ref={ref}>
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className='max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Edit Upcoming Transaction</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className='flex justify-center py-4'>
            <Loader2 className='size-6 animate-spin text-primary' />
          </div>
        ) : (
          <>
            {invoiceLineItems && invoiceLineItems.length > 0 && (
              <div className='mb-4'>
                <h3 className='mb-2 text-sm font-medium'>Line Items</h3>
                <EnrollioTable
                  totalLabel='Line items'
                  filterHeader={(table) => (
                    <EnrollioTableSearch
                      table={table}
                      placeholder='Search line items...'
                    />
                  )}
                  data={invoiceLineItems}
                  columns={columns}
                  getRowId={(row, index) =>
                    `${row.productName}-${row.type}-${String(index)}`
                  }
                  isUpdating={isPending}
                  paginationVariant='compact'
                  emptyContent='No line items.'
                  ariaLabel='Upcoming transaction line items'
                />
              </div>
            )}

            {/* Discount Section */}
            <InvoiceDiscountManager invoiceId={transactionId} />
          </>
        )}
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => {
              setIsOpen(false);
            }}
            disabled={isPending}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

EditUpcomingTransaction.displayName = 'EditUpcomingTransaction';
